import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import useTrainingsApi from './useTrainingsApi'

const getInitialPlacowkaId = searchParams =>
	searchParams.get('placowkaId') || searchParams.get('id_placowka') || null

export default function useTrainingsData() {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const initialPlacowkaId = getInitialPlacowkaId(searchParams)
	const { deleteTraining, getAllTrainings, searchTrainings } = useTrainingsApi()
	const defaultLimit = 25
	const limitIncrement = 25
	const defaultSortColumn = 'trainingDate'
	const defaultSortDirection = 'desc'

	const [data, setData] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [totalCount, setTotalCount] = useState(0)
	const [hasMore, setHasMore] = useState(false)
	const [limit, setLimit] = useState(defaultLimit)
	const [filters, setFilters] = useState(() => ({
		placowkaId: initialPlacowkaId,
	}))
	const [sortColumn, setSortColumn] = useState(defaultSortColumn)
	const [sortDirection, setSortDirection] = useState(defaultSortDirection)
	const loadingMoreRef = useRef(false)

	const loadData = useCallback(async () => {
		if (!loadingMoreRef.current) setLoading(true)
		setError(null)

		try {
			const searchText = `${filters.searchText || ''}`.trim()
			const requestOptions = {
				limit,
				sortColumn,
				sortDirection,
				filters: {
					status: filters.status || '',
					placowkaId: filters.placowkaId || '',
					trainerId: filters.trainerId || '',
				},
			}
			const result = searchText
				? await searchTrainings(searchText, requestOptions)
				: await getAllTrainings(requestOptions)

			if (result?.error) {
				setError(result.error)
				setData([])
				setTotalCount(0)
				setHasMore(false)
			} else {
				setData(Array.isArray(result?.data) ? result.data : [])
				setTotalCount(result?.totalCount || 0)
				setHasMore(!!result?.hasMore)
			}
		} catch (error) {
			console.error('useTrainingsData.loadData error:', error)
			setError(error?.message || 'Failed to load trainings')
			setData([])
			setTotalCount(0)
			setHasMore(false)
		} finally {
			loadingMoreRef.current = false
			setLoading(false)
			setLoadingMore(false)
		}
	}, [filters, getAllTrainings, limit, searchTrainings, sortColumn, sortDirection])

	const clearError = useCallback(() => setError(null), [])

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => clearError())
		}
	}, [clearError, error, t])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleLoadMore = useCallback(() => {
		if (hasMore && !loadingMoreRef.current) {
			loadingMoreRef.current = true
			setLimit(prev => prev + limitIncrement)
			setLoadingMore(true)
		}
	}, [hasMore])

	const setSorting = useCallback((column, direction) => {
		setSortColumn(column)
		setSortDirection(direction)
	}, [])

	const updateFilters = useCallback(newFilters => {
		setFilters(prev => ({ ...prev, ...newFilters }))
	}, [])

	const handleSort = useCallback(column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}, [setSorting, sortColumn, sortDirection])

	const handleSearchChange = useCallback(event => {
		updateFilters({ searchText: event.target.value })
	}, [updateFilters])

	const handleDelete = useCallback(async training => {
		const trainingName = training?.nazwa || training?.trainingName || '—'
		const result = await Swal.fire({
			title: t('trainings.list.deleteDialog.confirmTitle'),
			text: t('trainings.list.deleteDialog.confirmText', { name: trainingName }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('trainings.list.deleteDialog.confirmButton'),
			cancelButtonText: t('trainings.list.deleteDialog.cancelButton'),
			confirmButtonColor: '#dc2626',
		})

		if (!result.isConfirmed) return

		const deleteResult = await deleteTraining(training?._id)
		if (!deleteResult.success) {
			await Swal.fire({
				title: t('trainings.list.deleteDialog.errorTitle'),
				text: deleteResult.error || t('trainings.list.deleteDialog.errorFallback'),
				icon: 'error',
			})
			return
		}

		await loadData()
	}, [deleteTraining, loadData, t])

	return {
		data,
		totalCount,
		hasMore,
		loading,
		loadingMore,
		error,
		sortColumn,
		sortDirection,
		searchQuery: filters.searchText || '',
		loadData,
		handleLoadMore,
		handleSort,
		handleSearchChange,
		handleDelete,
		setSorting,
		updateFilters,
		clearError,
		initialPlacowkaId,
	}
}
